import { useCallback, useEffect, useRef, useState } from 'react';
import { getDocument, GlobalWorkerOptions, type PDFDocumentProxy } from 'pdfjs-dist';
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { renderAsync } from 'docx-preview';
import { embedSignatureOnDocx } from '../services/docxSign';
import { embedSignatureOnPdf, type SignaturePlacement } from '../services/pdfSign';
import { signedFileName } from '../services/signNames';
import { trimPngWhitespace } from '../utils/trimPng';
import './PdfSigner.css';

GlobalWorkerOptions.workerSrc = pdfWorker;

export type DocumentKind = 'pdf' | 'docx';

type Props = {
  fileName: string;
  kind: DocumentKind;
  documentBytes: Uint8Array;
  signaturePngUrl: string;
  signaturePngBytes: Uint8Array;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onApply: (signedBytes: Uint8Array, outName: string) => Promise<void>;
};

type DragMode = 'move' | 'resize';

const DEFAULT_WIDTH_FRAC = 0.28;

export function PdfSigner({
  fileName,
  kind,
  documentBytes,
  signaturePngUrl,
  signaturePngBytes,
  busy,
  error,
  onCancel,
  onApply,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const docxRef = useRef<HTMLDivElement>(null);
  const pageBoxRef = useRef<HTMLDivElement>(null);
  const pageWrapRef = useRef<HTMLDivElement>(null);
  const pdfRef = useRef<PDFDocumentProxy | null>(null);
  const dragMode = useRef<DragMode | null>(null);
  const dragOrigin = useRef({ x: 0, y: 0, left: 0, top: 0, w: 0, h: 0 });
  const pngAspectRef = useRef(2.5);
  const pageIndexRef = useRef(0);
  const placementRef = useRef<SignaturePlacement>({
    pageIndex: 0,
    leftFrac: 0.65,
    topFrac: 0.78,
    widthFrac: DEFAULT_WIDTH_FRAC,
    heightFrac: DEFAULT_WIDTH_FRAC / 2.5,
  });

  const [pageIndex, setPageIndex] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [pngAspect, setPngAspect] = useState(2.5);
  const [ready, setReady] = useState(false);
  const [placement, setPlacement] = useState<SignaturePlacement>(placementRef.current);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    placementRef.current = placement;
  }, [placement]);

  useEffect(() => {
    pageIndexRef.current = pageIndex;
  }, [pageIndex]);

  useEffect(() => {
    pngAspectRef.current = pngAspect;
  }, [pngAspect]);

  const clampPlacement = useCallback((next: SignaturePlacement): SignaturePlacement => {
    const widthFrac = Math.min(0.9, Math.max(0.08, next.widthFrac));
    const heightFrac = Math.min(0.95, Math.max(0.02, next.heightFrac));
    const leftFrac = Math.min(1 - widthFrac, Math.max(0, next.leftFrac));
    const topFrac = Math.min(1 - heightFrac, Math.max(0, next.topFrac));
    return { ...next, widthFrac, heightFrac, leftFrac, topFrac };
  }, []);

  /** Keep PNG aspect: heightFrac is relative to container H, widthFrac to W. */
  const syncHeightFromWidth = useCallback(
    (widthFrac: number, base?: SignaturePlacement) => {
      const cur = base ?? placementRef.current;
      const box = pageBoxRef.current?.getBoundingClientRect();
      const aspect = Math.max(pngAspectRef.current, 0.05);
      let heightFrac = widthFrac / aspect;
      if (box && box.width > 0 && box.height > 0) {
        heightFrac = (widthFrac * box.width) / (aspect * box.height);
      }
      return clampPlacement({
        ...cur,
        pageIndex: pageIndexRef.current,
        widthFrac,
        heightFrac,
      });
    },
    [clampPlacement],
  );

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        const aspect = img.naturalWidth / img.naturalHeight;
        setPngAspect(aspect);
        pngAspectRef.current = aspect;
        requestAnimationFrame(() => {
          const next = syncHeightFromWidth(placementRef.current.widthFrac);
          placementRef.current = next;
          setPlacement(next);
        });
      }
    };
    img.src = signaturePngUrl;
  }, [signaturePngUrl, syncHeightFromWidth]);

  useEffect(() => {
    if (!ready) return;
    const next = syncHeightFromWidth(placementRef.current.widthFrac);
    placementRef.current = next;
    setPlacement(next);
  }, [ready, kind, syncHeightFromWidth]);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setRenderError(null);
    setPageIndex(0);
    setPageCount(0);

    (async () => {
      try {
        if (kind === 'pdf') {
          const loading = getDocument({ data: documentBytes.slice() });
          const pdf = await loading.promise;
          if (cancelled) {
            await pdf.cleanup();
            return;
          }
          if (pdfRef.current) await pdfRef.current.cleanup();
          pdfRef.current = pdf;
          setPageCount(pdf.numPages);
          setPlacement((p) => {
            const next = { ...p, pageIndex: 0 };
            placementRef.current = next;
            return next;
          });
        } else {
          pdfRef.current = null;
          await new Promise((r) => requestAnimationFrame(() => r(null)));
          const host = docxRef.current;
          const stage = pageWrapRef.current;
          const box = pageBoxRef.current;
          if (!host) {
            throw new Error('DOCX-Vorschau konnte nicht geladen werden.');
          }
          host.innerHTML = '';
          host.style.transform = '';
          host.style.width = '';
          if (box) {
            box.style.width = '';
            box.style.height = '';
          }
          await renderAsync(new Blob([new Uint8Array(documentBytes)]), host, undefined, {
            className: 'ps-docx',
            inWrapper: true,
            ignoreWidth: false,
            breakPages: true,
            useBase64URL: true,
          });
          if (cancelled) return;

          // Scale A4 page down to taskpane width (avoids huge side margins)
          const pageEl =
            (host.querySelector('section.docx') as HTMLElement | null) ||
            (host.firstElementChild as HTMLElement | null);
          const naturalW = Math.max(pageEl?.offsetWidth || 0, host.scrollWidth, 1);
          const naturalH = Math.max(host.scrollHeight, pageEl?.offsetHeight || 0, 1);
          const avail = Math.max((stage?.clientWidth ?? box?.clientWidth ?? 360) - 4, 120);
          const scale = Math.min(1, avail / naturalW);

          host.style.width = `${naturalW}px`;
          host.style.transformOrigin = 'top left';
          host.style.transform = `scale(${scale})`;
          if (box) {
            box.style.width = `${Math.round(naturalW * scale)}px`;
            box.style.height = `${Math.round(naturalH * scale)}px`;
            box.style.overflow = 'hidden';
          }

          setPageCount(1);
          setReady(true);
        }
      } catch (e) {
        if (!cancelled) setRenderError(e instanceof Error ? e.message : String(e));
      }
    })();

    return () => {
      cancelled = true;
      void pdfRef.current?.cleanup();
      pdfRef.current = null;
      if (docxRef.current) docxRef.current.innerHTML = '';
    };
  }, [documentBytes, kind]);

  const renderPage = useCallback(async (index: number) => {
    const pdf = pdfRef.current;
    const canvas = canvasRef.current;
    if (!pdf || !canvas) return;
    const page = await pdf.getPage(index + 1);
    const base = page.getViewport({ scale: 1 });
    const maxWidth = pageWrapRef.current?.clientWidth || 360;
    const scale = Math.min(1.4, maxWidth / base.width);
    const viewport = page.getViewport({ scale });
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    setReady(true);
    await page.render({ canvas, canvasContext: ctx, viewport }).promise;
  }, []);

  useEffect(() => {
    if (kind === 'pdf' && pageCount > 0) void renderPage(pageIndex);
  }, [kind, pageCount, pageIndex, renderPage]);

  const stopDrag = useCallback(() => {
    dragMode.current = null;
  }, []);

  const onDocPointerMove = useCallback(
    (e: PointerEvent) => {
      if (!dragMode.current) return;
      const box = pageBoxRef.current?.getBoundingClientRect();
      if (!box || box.width <= 0 || box.height <= 0) return;
      const dx = (e.clientX - dragOrigin.current.x) / box.width;
      const dy = (e.clientY - dragOrigin.current.y) / box.height;
      if (dragMode.current === 'move') {
        const next = clampPlacement({
          ...placementRef.current,
          pageIndex: pageIndexRef.current,
          leftFrac: dragOrigin.current.left + dx,
          topFrac: dragOrigin.current.top + dy,
        });
        placementRef.current = next;
        setPlacement(next);
      } else {
        const next = syncHeightFromWidth(dragOrigin.current.w + dx);
        placementRef.current = next;
        setPlacement(next);
      }
    },
    [clampPlacement, syncHeightFromWidth],
  );

  useEffect(() => {
    const up = () => stopDrag();
    window.addEventListener('pointermove', onDocPointerMove);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointermove', onDocPointerMove);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, [onDocPointerMove, stopDrag]);

  const beginDrag = (mode: DragMode, e: React.PointerEvent) => {
    if (busy || saving) return;
    e.preventDefault();
    e.stopPropagation();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    dragMode.current = mode;
    dragOrigin.current = {
      x: e.clientX,
      y: e.clientY,
      left: placementRef.current.leftFrac,
      top: placementRef.current.topFrac,
      w: placementRef.current.widthFrac,
      h: placementRef.current.heightFrac,
    };
  };

  const apply = async () => {
    setSaving(true);
    try {
      const place = syncHeightFromWidth(placementRef.current.widthFrac, {
        ...placementRef.current,
        pageIndex,
      });
      placementRef.current = place;
      setPlacement(place);
      const png = await trimPngWhitespace(signaturePngBytes);
      const signed =
        kind === 'docx'
          ? await embedSignatureOnDocx(documentBytes, png, place)
          : await embedSignatureOnPdf(documentBytes, png, place);
      await onApply(signed, signedFileName(fileName));
    } finally {
      setSaving(false);
    }
  };

  const overlayStyle: React.CSSProperties = {
    left: `${placement.leftFrac * 100}%`,
    top: `${placement.topFrac * 100}%`,
    width: `${placement.widthFrac * 100}%`,
    height: 'auto',
    aspectRatio: `${Math.max(pngAspect, 0.05)}`,
  };

  return (
    <div className="ps">
      <div className="ps-toolbar">
        <button type="button" className="sp-btn sp-btn--secondary" disabled={busy || saving} onClick={onCancel}>
          Zurück
        </button>
        <div className="ps-title" title={fileName}>
          {fileName}
        </div>
      </div>

      <p className="ps-hint">
        Unterschrift am Rahmen ziehen zum Verschieben. Blaue Ecke zum Vergrößern.
        {kind === 'docx' ? ' (Nur .docx — alte .doc-Dateien werden nicht unterstützt.)' : ''}
      </p>

      {kind === 'pdf' ? (
        <div className="ps-pages">
          <button
            type="button"
            className="sp-btn sp-btn--secondary"
            disabled={pageIndex <= 0 || busy || saving}
            onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
          >
            ← Seite
          </button>
          <span className="ps-page-label">
            Seite {pageCount ? pageIndex + 1 : '—'} / {pageCount || '—'}
          </span>
          <button
            type="button"
            className="sp-btn sp-btn--secondary"
            disabled={pageIndex >= pageCount - 1 || busy || saving}
            onClick={() => setPageIndex((p) => Math.min(pageCount - 1, p + 1))}
          >
            Seite →
          </button>
        </div>
      ) : (
        <div className="ps-pages">
          <span className="ps-page-label">Word (.docx)</span>
        </div>
      )}

      <div className="ps-stage" ref={pageWrapRef}>
        <div ref={pageBoxRef} className={`ps-page${kind === 'docx' ? ' ps-page--docx' : ''}`}>
          {kind === 'pdf' ? (
            <canvas ref={canvasRef} className="ps-canvas" />
          ) : (
            <div ref={docxRef} className="ps-docx-host" />
          )}
          {ready ? (
            <div
              className="ps-sig"
              style={overlayStyle}
              onPointerDown={(e) => beginDrag('move', e)}
              role="img"
              aria-label="Unterschrift verschieben"
            >
              <img src={signaturePngUrl} alt="" draggable={false} />
              <span
                className="ps-resize"
                onPointerDown={(e) => beginDrag('resize', e)}
                aria-label="Größe ändern"
              />
            </div>
          ) : null}
        </div>
      </div>

      {(renderError || error) && (
        <div className="sp-error" role="alert">
          <p>{renderError || error}</p>
        </div>
      )}

      <button
        type="button"
        className="sp-btn sp-btn--primary ps-apply"
        disabled={busy || saving || !!renderError || !ready}
        onClick={() => void apply()}
      >
        {saving ? 'Signieren…' : 'Signieren & Antwort öffnen'}
      </button>
    </div>
  );
}
